import { ApiPaymentDriftChange } from "./types";
import { fmtCcy, formatFriendly } from "./helpers";

// Single source of truth for rendering a payment drift change, shared by the
// rerun modal and the Superseded tab so the two can't drift out of sync the
// way their labels already had (see CHANGE_LABELS below).
export const CHANGE_LABELS: Record<string, string> = {
  added: "Newly present",
  removed: "No longer present",
  amount_changed: "Amount changed",
  details_changed: "Details amended",
  transaction_date_changed: "Transaction date changed",
  share_class_changed: "Share class changed",
  status_changed: "Match status changed",
};

const STATUS_LABELS: Record<string, string> = {
  matched: "Matched",
  unit_discrepancy: "Unit discrepancy",
  unmatched_row: "Unmatched transaction",
  unmatched_payment: "Unmatched payment",
  incomplete_split: "Split short",
  split_exceeds: "Payments exceed deposit",
  split_ambiguous: "Unclear narration",
};

const statusLabel = (status: string | null): string =>
  status ? STATUS_LABELS[status] ?? status : "—";

// Every kind but details_changed used to fall back to previousAmount/
// currentAmount, which the backend's compare() (transaction_drift_service.rb /
// pending_drift_service.rb) only reaches those branches when the amount is
// unchanged — so a status or date change rendered as "$X → $X" and looked like
// nothing had moved.
export function describeChange(change: ApiPaymentDriftChange): {
  before: string;
  after: string;
} {
  const DASH = "—";
  switch (change.change) {
    case "details_changed":
      return {
        before: change.previousDetails ?? DASH,
        after: change.currentDetails ?? DASH,
      };
    case "transaction_date_changed":
      return {
        before: change.previousTransactionDate
          ? formatFriendly(change.previousTransactionDate)
          : DASH,
        after: change.currentTransactionDate
          ? formatFriendly(change.currentTransactionDate)
          : DASH,
      };
    case "share_class_changed":
      return {
        before: change.previousShareClass ?? DASH,
        after: change.currentShareClass ?? DASH,
      };
    case "status_changed":
      return {
        before: statusLabel(change.previousStatus),
        after: statusLabel(change.currentStatus),
      };
    default:
      return {
        before:
          change.previousAmount != null ? fmtCcy(change.previousAmount) : DASH,
        after:
          change.currentAmount != null ? fmtCcy(change.currentAmount) : DASH,
      };
  }
}
